import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { TranslationProvider, useTranslation } from '@ibk/shared-translation';
import { InlineError, Input } from '@ibk/shared-ui';
import translations from '../../translations';
import styles from './TwoFactorVerify.module.css';
import { useTwoFactorVerify } from './useTwoFactorVerify';
const defaultTexts = {
    title: '',
    smsInstruction: '',
    emailInstruction: '',
    resend: '',
    resendWait: '',
    resendLink: '',
    continue: '',
    back: '',
    error: '',
};
export function TwoFactorVerifyContent({ maskedRecipient, onBack, onContinue, onResend, disabled = false, error: externalError = null, theme = {}, texts = {}, method = 'phone', }) {
    const { t } = useTranslation();
    const mergedTexts = {
        ...defaultTexts,
        title: t('auth.2fa.verify.title'),
        smsInstruction: t('auth.2fa.verify.sms-instruction'),
        emailInstruction: t('auth.2fa.verify.email-instruction'),
        resend: t('auth.2fa.verify.resend'),
        resendWait: (seconds) => t('auth.2fa.verify.resend-wait', { seconds }),
        resendLink: t('auth.2fa.verify.resend-link'),
        continue: t('auth.2fa.verify.continue'),
        back: t('auth.2fa.verify.back'),
        error: t('auth.2fa.verify.error'),
        ...texts,
    };
    const { values, handleChange, handleContinue, handleResendCode, disabled: isDisabled, displayError, showInlineError, setShowInlineError, showResendMessage, canResend, countdown, inputsRef, } = useTwoFactorVerify({
        onContinue,
        onResend,
        disabled,
        error: externalError,
    });
    const handleEnter = () => {
        if (values.join('').length !== 6 || isDisabled)
            return;
        handleContinue();
    };
    const styleVars = {
        '--color-primary': theme.primaryColor ?? undefined,
        '--link-default': theme.linkColor ?? undefined,
        '--color-surface': theme.cardBackground ?? undefined,
        '--text-field-surface': theme.background ?? undefined,
    };
    const inputIds = ['a', 'b', 'c', 'd', 'e', 'f'];
    return (_jsx("div", { className: styles.verifyRoot, style: styleVars, children: _jsxs("div", { className: styles.verifyCardWrapper, children: [_jsxs("div", { className: styles.verifyFormTitle, children: [(method === 'phone' ? mergedTexts.smsInstruction : mergedTexts.emailInstruction) + ' ', _jsx("b", { children: maskedRecipient })] }), _jsx("div", { className: styles.codeInputGroup, children: values.map((val, idx) => (_jsx(Input, { inputRef: inputsRef.current[idx], type: 'text', maxLength: 1, value: val, "aria-label": `2FA code input number ${idx + 1}`, onChange: e => handleChange(idx, e.target.value), dataTestId: `2fa-code-input-${idx}`, onKeyDown: e => {
                            if (e.key === 'Backspace' && !values[idx] && idx > 0) {
                                inputsRef.current[idx - 1]?.current?.focus();
                            }
                            if (e.key === 'Enter') {
                                handleEnter();
                            }
                        }, size: 'large', className: styles.pinInput, autoFocus: idx === 0 }, `code-input-${inputIds[idx]}`))) }), showInlineError && displayError && (_jsx(InlineError, { message: displayError, onClose: () => setShowInlineError(false), "data-testid": '2fa-inline-error' })), showResendMessage && (_jsx("div", { className: styles.resendMessage, "data-testid": '2fa-message-resend', children: _jsxs("span", { children: [mergedTexts.resend, " \u00A0", canResend ? (_jsx("button", { type: 'button', onClick: handleResendCode, className: styles.resendLink, "data-testid": '2fa-resend-link', children: mergedTexts.resendLink })) : (_jsx("span", { className: styles.countdownText, "data-testid": '2fa-countdown', children: mergedTexts.resendWait(countdown) }))] }) })), _jsx("button", { type: 'button', className: styles.verifyButton, onClick: handleContinue, disabled: values.join('').length !== 6 || isDisabled, "data-testid": '2fa-verify-button', children: isDisabled ? t('auth.2fa.verify.validating') : mergedTexts.continue }), _jsx("button", { type: 'button', className: styles.backButton, onClick: onBack, disabled: isDisabled, "data-testid": '2fa-back-button', children: mergedTexts.back })] }) }));
}
export function TwoFactorVerify(props) {
    return (_jsx(TranslationProvider, { translations: translations, defaultLanguage: 'pt-BR', children: _jsx(TwoFactorVerifyContent, { ...props }) }));
}
